// Event Tracker Class
/**
 * CustomEventTracker class for tracking DOM events and custom events.
 */
class CustomEventTracker {
  static instances = {}; // Static property to keep track of instances by eventName

  /**
   * Static method to get all instances of CustomEventTracker.
   * @returns {Array<CustomEventTracker>} - An array of all CustomEventTracker instances.
   */
  static getAllInstances() {
    return Object.values(CustomEventTracker.instances);
  }

  /**
   * Creates an instance of the CustomEventTracker.
   * @param {string} eventName - The name of the event to track.
   * @param {string} eventType - The type of DOM event (e.g., 'click') or a custom event name.
   * @param {Array<string>} selectors - An array of CSS selectors to attach event listeners to.
   * @param {Object} [options] - Optional configuration options.
   * @param {boolean} [options.DEBUG=false] - Enable debug mode to log events to the console.
   */
  constructor(eventName, eventType, selectors, options = {}) {
    if (CustomEventTracker.instances[eventName]) {
      throw new Error(`An instance with event name "${eventName}" already exists.`);
    }

    this.eventName = eventName; // The name of the event
    this.eventType = eventType; // The type of DOM event or custom event name
    this.selectors = selectors; // Array of CSS selectors
    this.listeners = []; // Store registered listeners
    this.DEBUG = options.DEBUG || false; // DEBUG mode
    this.instanceName = eventName; // Set instance name for logging

    // Register the instance in the static property
    this.log('Initialized');
    CustomEventTracker.instances[eventName] = this;
  }

  /**
   * Logs messages to the console in bold and vibrant blue if DEBUG mode is enabled.
   * @param {...*} args - The messages or data to log.
   */
  log(...args) {
    if (this.DEBUG) {
      try {
        const styles = 'font-weight: bold; color: #007BFF;';
        const onlyStringArgs = args.filter((arg) => typeof arg === 'string');
        const restOfArgs = args.filter((arg) => typeof arg !== 'string');
        const instanceName = this.instanceName ? this.instanceName : '';
        console.log(
          `%c[CustomEventTracker] :: ${instanceName} :: ${onlyStringArgs.join(' | ')} `,
          styles,
          ...restOfArgs
        );
      } catch (e) {
        console.log(...args); // Default to console.log if an error occurs
      }
    }
  }

  /**
   * Handles the event when triggered.
   * @param {Event} event - The original event object.
   */
  handleEvent(event) {
    this.log('Event Triggered', { event });
    this.emit(event);
  }

  /**
   * Emits the event data to all registered listeners.
   * @param {Event} event - The event object to emit.
   */
  emit(event) {
    this.listeners.forEach((callback) => {
      try {
        callback(event); // Pass the original event object to the callback
      } catch (error) {
        console.error('Error in subscriber callback:', error); // Log the error in default color
      }
    });
  }
  /**
   * Subscribes a callback to be invoked when the event occurs.
   *
   * @param {Object} options - The options object containing the callback and name.
   * @param {Function} options.callback - The callback function to be registered.
   * @param {string} [options.name] - The name of the subscriber.
   */
  subscribe({ callback, name }) {
    if (!callback._id) {
      callback._id = `custom_track__${Math.random().toString(36).substring(2, 11)}`;
    }
    callback._name = name || 'Anonymous';
    this.listeners.push(callback);
    this.log('Subscriber Added', callback._id, callback._name);
  }

  /**
   * Unsubscribes a callback from being invoked when the event occurs.
   *
   * @param {string} id - The ID of the subscriber to be removed.
   */
  unsubscribe(id) {
    const listener = this.listeners.find((listener) => listener._id === id);
    if (listener) {
      this.listeners = this.listeners.filter((listener) => listener._id !== id);
      this.log('Subscriber Removed:', id, listener._name);
    } else {
      this.log('Subscriber not found:', id);
    }
  }

  /**
   * Gets a list of all subscribers with their names.
   *
   * @returns {Array<string>} - An array of subscriber names.
   */
  getSubscribers() {
    return this.listeners.map((listener) => ({
      name: listener._name || 'Anonymous',
      id: listener._id,
    }));
  }

  /**
   * Initializes the CustomEventTracker.
   *
   * @param {Object} [options={}] - Object of configuration options.
   * @param {string} [options.initEvent='DOMContentLoaded'] - The event that triggers initialization.
   * @param {Object} [options.observe=null] - Wheter to add DOM mutation observer.
   * @param {string} [options.observe.targetNodeSelector='body'] - The CSS selector for the target node to observe.
   * @param {boolean} [options.observe.keepLive=true] - Whether to keep the observer live.
   *
   * @example
   * const tracker = new CustomEventTracker();
   * tracker.init({
   *   initEvent: 'load',
   *   observe: {
   *     targetNodeSelector: '#app',
   *     keepLive: false
   *   }
   * });
   */
  init(options = {}) {
    const { initEvent = 'DOMContentLoaded', observe = null } = options;
    const addEventToElement = (addedFrom) => {
      this.selectors.forEach((selector) => {
        const targets = document.querySelectorAll(selector);
        const added = [];
        targets?.forEach((element) => {
          if (element.hasAttribute('custom-event-tracked')) return;
          element.addEventListener(this.eventType, (event) => {
            this.handleEvent(event); // Pass the original event object
          });
          element.setAttribute('custom-event-tracked', true);
          added.push(element);
        });
        added.length > 0 && this.log('CustomEventTracker initialized on', initEvent);
      });
    };

    document.addEventListener(initEvent, addEventToElement(initEvent));

    if (observe) {
      const { targetNodeSelector = 'body', keepLive = true } = observe;
      const targetNode = document.querySelector(targetNodeSelector);
      if (!targetNode) return;

      const config = { childList: true, subtree: true };

      const callback = (mutationsList, observer) => {
        mutationsList.forEach((mutation) => {
          if (mutation.type === 'childList') {
            addEventToElement('mutationObserver');
          }
        });
      };

      const observer = new MutationObserver(callback);
      observer.observe(targetNode, config);

      if (!keepLive) {
        observer.disconnect();
      }
    }
  }

  /**
   * Kills the instance of CustomEventTracker by removing event listeners and deleting the instance.
   */
  kill() {
    // Remove event listeners
    this.selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        element.removeEventListener(this.eventType, this.handleEvent);
      });
    });

    // Delete the instance from the instances object
    delete CustomEventTracker.instances[this.eventName];

    this.log('CustomEventTracker instance killed');
  }
}

/* Example usage of the CustomEventTracker with DEBUG mode enabled */
// const buttonClickTracker = new CustomEventTracker('button_click', 'click', ['button[name=add]', 'a'], { DEBUG: true });
// Register a listener to handle emitted events
// buttonClickTracker.subscribe({
// callback: (event) => {
// buttonClickTracker.log('Button Clicked:', event.currentTarget);
//  },
// name: `GA Event Tracker`,
// });

// Initialize the tracker
// buttonClickTracker.init({
// observe: {
// keepLive: true,
// targetNodeSelector: 'body',
// },
// });

/*
 * Add To Cart Custom Event Tracker
 * It is going to use the PUB_SUB_EVENT default event from theme
 */

window.addEventListener('cart_changed', (event) => {
  const centsToAmount = (o) => {
    if (typeof o === 'number' && o > 0) return o / 100;
    return 0;
  };

  const sendAnalytics = (newItem) => {
    const event_data = {
      cartLine: {
        cost: {
          totalAmount: {
            amount: centsToAmount(newItem.original_line_price),
            currencyCode: Shopify?.currency.active ?? 'USD',
          },
        },
        merchandise: {
          id: newItem.id,
          image: {
            src: newItem.image ?? null,
          },
          price: {
            amount: centsToAmount(newItem.price),
            currencyCode: Shopify?.currency.active ?? 'USD',
          },
          product: {
            id: newItem.product_id,
            title: newItem.product_title,
            type: newItem.product_type,
            vendor: newItem.vendor,
            untranslatedTitle: newItem.product_title,
            url: newItem.url,
          },
          sku: newItem.sku,
          title: newItem.product_title,
          untranslatedTitle: newItem.untranslated_product_title,
        },
        quantity: newItem.quantity,
      },
    };

    Shopify.analytics.publish('custom_add_to_cart', event_data);
  };

  if (event?.detail?.changes?.added?.length > 0) {
    // debugger;
    try {
      event?.detail?.changes?.added.forEach((newItem) => {
        if (newItem.properties.strikethrough || newItem.properties._strikethrough) return;
        sendAnalytics(newItem);
      });
    } catch (e) {
      console.error(e);
    }
  }
});
